import axios from 'axios';
import { useCallback, useState } from 'react';

import { useApi } from '../../api/useApi.hook';

interface UseUploadFileProps {
    documentId: string;
    onUploadComplete?: (file: UploadedFile) => void;
    onUploadError?: (error: unknown) => void;
    headers?: Record<string, string>;
    onUploadBegin?: (fileName: string) => void;
    onUploadProgress?: (progress: { progress: number }) => void;
    skipPolling?: boolean;
}
   
interface UploadedFile {
    key: string;    // Unique identifier
    url: string;    // Public URL of the uploaded file
    name: string;   // Original filename
    size: number;   // File size in bytes
    type: string;   // MIME type
}

export function useUploadFileFunction(documentId: string) {
    const api = useApi();

    const upload = useCallback(async (file: File) => {
        const uploadLink = await api.v1.documents.getMediaUploadLink(documentId, {
            filename: file.name,
            mimeType: file.type,
        });

        // // Upload to S3 using presigned URL
        await axios.put(uploadLink.url, file, {
            headers: { 'Content-Type': file.type },
        });

        const fileKey = uploadLink.objectKey;
        const fileUrl = fileKey;
    
        return fileUrl;
    }, [ api, documentId ]);

    return upload;
}

export function useUploadFile({
    documentId,
    onUploadComplete, 
    onUploadError, 
    onUploadProgress 
}: UseUploadFileProps) {
    const api = useApi();
    const [uploadedFile, setUploadedFile] = useState<UploadedFile>();
    const [uploadingFile, setUploadingFile] = useState<File>();
    const [progress, setProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
   
    async function uploadFile(file: File) {
        setIsUploading(true);
        setUploadingFile(file);
   
        try {            
            const uploadLink = await api.v1.documents.getMediaUploadLink(documentId, {
                filename: file.name,
                mimeType: file.type,
            });
   
            // // Upload to S3 using presigned URL
            await axios.put(uploadLink.url, file, {
                headers: { 'Content-Type': file.type },
                onUploadProgress: (progressEvent) => {
                    const progress = progressEvent.total !== undefined
                        ? (progressEvent.loaded / progressEvent.total) * 100
                        : 0;
                    setProgress(progress);
                    onUploadProgress?.({ progress });
                },
            });

            const fileKey = uploadLink.objectKey;
            const fileUrl = fileKey;

            const uploadedFile = {
                key: fileKey,
                url: fileUrl,
                name: file.name,
                size: file.size,
                type: file.type,
            };
   
            setUploadedFile(uploadedFile);
            onUploadComplete?.(uploadedFile);
        
            return uploadedFile;
        } catch (error) {
            onUploadError?.(error);
            throw error;
        } finally {
            setProgress(0);
            setIsUploading(false);
            setUploadingFile(undefined);
        }
    }
   
    return {
        isUploading,
        progress,
        uploadFile,
        uploadedFile,
        uploadingFile,
    };
}
